import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import './DocumentUploadForm.css';
import LargeFormPanel from '../LargeFormPanel';
import InputField from '../../../components/InputField';
import ExpandingTextArea from '../../../components/ExpandingTextArea';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { UserUtils } from '../../../../utils/UserUtils';
import UserTokenData from '../../../../types/UserTokenData';
import PresignedUrlResponse from '../../../../types/PresignedUrlResponse';
import FormButton from '../../../components/FormButton/FormButton';
import BlockingLoader from '../../../components/BlockingLoader/BlockingLoader';
import { IoClose } from 'react-icons/io5';

interface Props {
  token: string;
  userDetails: UserTokenData;
  entityId: number;
  nav: NavigateFunction;
  onUploadSuccess: () => Promise<void>;
}

interface UploadFormData {
  requestedBy: string;
  entityId: number;
  title: string;
  uploadFile: File | null;
  fileType: string;
  titleError: string;
  fileError: string;
  formError: string;
  networkError: string;
}

function DocumentUploadForm({
  token,
  userDetails,
  entityId,
  nav,
  onUploadSuccess,
}: Props): JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<UploadFormData>({
    requestedBy: userDetails.id,
    entityId: entityId,
    title: '',
    uploadFile: null,
    fileType: '',
    titleError: '',
    fileError: '',
    formError: '',
    networkError: '',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadDocument = async () => {
    if (formData.formError != '') return;
    if (formData.fileError != '') {
      setFormData({
        ...formData,
        formError: `Please address errors.`,
      });
      return;
    }
    if (formData.titleError != '') {
      setFormData({
        ...formData,
        formError: `Please address errors.`,
      });
      return;
    }
    if (formData.title == '') {
      setFormData({
        ...formData,
        formError: `A title is required`,
      });
      return;
    }
    if (formData.uploadFile == null) {
      setFormData({
        ...formData,
        formError: `A document is required, either .txt or .pdf`,
      });
      return;
    }

    setLoading(true);
    let presignedUrlResponse: PresignedUrlResponse | null = null;
    try {
      presignedUrlResponse = await handleRequestUploadUrl();
    } catch (error: any) {
      if (error.message) {
        setFormData({
          ...formData,
          networkError: error.message,
        });
      }
      console.log(`An error occurred when trying to get URL to upload to`);
      console.log(error);
      setLoading(false);
      return;
    }
    let success: boolean = false;
    try {
      if (presignedUrlResponse == null) {
        throw Error(`We somehow had an invalid presigned url`);
      }
      success = await handleUploadFileToURL(
        presignedUrlResponse.presignedUrl,
        formData.uploadFile
      );
    } catch (error: any) {
      success = false;
    }
    try {
      await notifyUploadBackend(success, presignedUrlResponse.jobId);
    } catch (error: any) {
      if (error.message) {
        setFormData({
          ...formData,
          networkError: error.message,
        });
      }
      setLoading(false);
      return;
    }
    onUploadSuccess();
    handleFormClear();
    setLoading(false);
  };

  const handleFormClear = () => {
    setFormData({
      requestedBy: userDetails.id,
      entityId: entityId,
      title: '',
      uploadFile: null,
      fileType: '',
      titleError: '',
      fileError: '',
      formError: '',
      networkError: '',
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRequestUploadUrl = async () => {
    const jsonData = JSON.stringify({
      requestedBy: formData.requestedBy,
      title: formData.title,
      entityId: formData.entityId,
      fileType: formData.fileType,
    });
    const endpointUrl = '/case/upload/url';
    const response = await fetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: jsonData,
    });
    if (!response.ok) {
      if (response.status === 401) {
        nav('/signin');
      }
      if (response.status === 400) {
        throw Error(`There may be an invalid value provided`);
      }
      throw Error(`Unexpected error, check logs for more detail`);
    }
    const result = await response.json();
    const jobId = Number(result.jobId);
    const presignedUrl = result.presignedUrl;
    return { presignedUrl, jobId };
  };

  const handleUploadFileToURL = async (
    endpointUrl: string,
    uploadFile: File
  ) => {
    try {
      const uploadResponse = await fetch(endpointUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: { 'Content-Type': uploadFile.type },
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.log(
          `Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`
        );
        console.log(`Response body: ${errorText}`);
        console.log(
          `If it was Failed to Fetch, double check the S3 bucket CORS policy`
        );
        return false;
      }

      return true;
    } catch (err) {
      console.log('Network or fetch error during upload:\n', err);
      return false;
    }
  };

  const notifyUploadBackend = async (success: boolean, jobId: number) => {
    // TODO set the correct URL here
    const endpointUrl = '/case/upload/validate';
    const jsonData = JSON.stringify({
      success: success,
      jobId: jobId,
    });
    const response = await fetch(endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: jsonData,
    });
    if (success && response.status == 404) {
      throw Error(
        `We were able to upload but were unable to find the file when it got uploaded`
      );
    }
    if (success && response.status == 200) {
      return;
    }
    if (!success && response.status == 200) {
      throw Error(`We were unable to upload the file!`);
    }
    if (response.status == 500) {
      throw Error(
        `There was a critical server error, you may want to try later`
      );
    }
    throw Error(
      `How in the hell did we get here, this shouldn't be possible right?`
    );
  };

  const handleUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;

    if (!file) {
      setFormData((f) => ({
        ...f,
        uploadFile: null,
        fileType: '',
        fileError: '',
      }));
      return;
    }

    const okMime = ['application/pdf', 'text/plain'];
    const ok = okMime.includes(file.type) || /\.(pdf|txt)$/i.test(file.name);

    if (!ok) {
      setFormData((f) => ({
        ...f,
        uploadFile: null,
        fileType: '',
        fileError: 'Only .pdf or .txt files are allowed.',
      }));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const fullName = file.name.toLowerCase();
    const fileName = fullName.substring(0, fullName.lastIndexOf('.'));

    setFormData((f) => ({
      ...f,
      uploadFile: file,
      fileType: file.type,
      fileError: '',
      title: f.title == '' ? fileName : f.title,
    }));
  };

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      title: event.target.value,
    }));
  };

  const handleRemoveUploadedFile = () => {
    setFormData((file) => ({
      ...file,
      uploadFile: null,
      fileType: '',
      fileError: '',
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // useEffect(() => {
  //   if (formData.uploadFile === null && fileInputRef.current) {
  //     fileInputRef.current.value = '';
  //   }
  // }, [formData.uploadFile]);

  return (
    <div className="DocumentUploadFormContent">
      <div className="DocumentUploadFormContentRow">
        <span>Document Title</span>
      </div>
      <div className="DocumentUploadFormContentRow">
        <InputField
          value={formData.title}
          label={'Enter Document Title'}
          onValueChange={handleTitleChange}
        ></InputField>
      </div>
      {formData.formError != '' && (
        <div className="DocumentUploadFormContentRow">
          <span>{formData.formError}</span>
        </div>
      )}
      {/* //TODO: need to change backend and frontend to upload multiple files */}
      <div className="document-upload-file-section">
        <h4 className="document-upload-file-name">Upload Documents</h4>
        <div className="DocumentUploadFormFileRow">
          <label
            htmlFor="DocumentFileUploadButton"
            className="DocumentUploadFileLabel"
          >
            CHOOSE
          </label>
          <input
            id="DocumentFileUploadButton"
            className="UploadTranscriptInput"
            type="file"
            accept=".txt,.pdf,application/pdf,text/plain"
            ref={fileInputRef}
            onChange={handleUploadFileChange}
          />
        </div>
        <div className="document-upload-file-add">
          {formData.uploadFile && (
            <span className="UploadTranscriptFileName">
              {formData.uploadFile.name}
            </span>
          )}
          {formData.uploadFile && (
            <button onClick={handleRemoveUploadedFile}>
              <IoClose className="file-upload-removal" />
            </button>
          )}
        </div>
      </div>

      {formData.fileError && (
        <div className="DocumentUploadFormContentRow">
          <span className="ErrorText">{formData.fileError}</span>
        </div>
      )}
      {formData.formError != '' && (
        <div className="DocumentUploadFormContentRow">
          <span>{formData.formError}</span>
        </div>
      )}
      {formData.formError != '' && (
        <div className="DocumentUploadFormContentRow">
          <span>{formData.formError}</span>
        </div>
      )}
      <div className="DocumentUploadFormSubmitRow">
        <button
          className="FormButtonEnabled document-upload-clear-btn"
          onClick={handleFormClear}
        >
          CANCEL
        </button>
        <button
          className="FormButtonEnabled document-upload-submit-btn"
          onClick={handleUploadDocument}
        >
          UPLOAD & SAVE
        </button>
      </div>
      {loading && <BlockingLoader />}
    </div>
  );
}

export default DocumentUploadForm;
